import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useSessionNoteChangeHandler from '@hooks/useSessionNoteChangeHandler';
import { useAudioTranscription } from '@hooks/useAudioTranscription';
import NoteEditor from '@components/Clinicians/Session/Components/NoteEditor';
import useClinicalNotesMeta from '@hooks/useClinicalNotesMeta';
import { SessionFormItem } from './SessionFormItem';
import { useHeidiEnabled } from '@hooks/useHeidiEnabled';
import { useHeidiSession } from '../hooks/useHeidiSession';
import useCallStatus from '@hooks/useCallStatus';

const PresentingConditionFormItem = () => {
  const { t } = useTranslation('common');
  const { isAudioError } = useAudioTranscription();

  const { appointment } = useSessionAppointment();
  const videoCallStatus = useCallStatus(appointment);
  const { read_only: readOnly, appointment_completed: apptCompleted, appointment_id: apptId } = appointment;

  const aiNotesAppt = useHeidiEnabled(apptId);

  const { status: aiNotesStatus, notes, hasHeidiError } = useHeidiSession();
  const handleNoteChange = useSessionNoteChangeHandler({ field: 'HPC' });
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (aiNotesStatus === 'completed' && notes?.hpc && !hasSyncedRef.current) {
      hasSyncedRef.current = true;
      handleNoteChange({ field: 'HPC', value: notes.hpc });
    }
  }, [aiNotesStatus, notes?.hpc]);

  const { getIsFieldRequired, getNoteLabel } = useClinicalNotesMeta();
  const isRequired = getIsFieldRequired('HPC');

  const shouldHideNotes = () => {
    if (!aiNotesAppt) return false;
    if (videoCallStatus === 'completed' && aiNotesStatus === 'idle') return false;
    return !isAudioError && !hasHeidiError && !apptCompleted && aiNotesStatus !== 'completed';
  };

  const hideNotes = shouldHideNotes();

  return (
    <SessionFormItem
      data-testid="clinical-hpc"
      key="hpc-form-item"
      label={getNoteLabel('HPC')}
      required={isRequired}
      labelCol={{ span: 24 }}
      hidden={hideNotes}
    >
      <NoteEditor
        data-testid="clinical-hpc"
        field="HPC"
        ariaLabel={getNoteLabel('HPC')}
        placeholder={t('clinicalHPCPlaceholder')}
        readOnly={readOnly || apptCompleted}
        onChange={handleNoteChange}
      />
    </SessionFormItem>
  );
};

export default PresentingConditionFormItem;
